import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronRight, GitBranch } from "lucide-react";
import {
  useCreateNodeGroup,
  useDeleteNodeGroup,
  useNodeGroups,
  useUpdateNodeGroup,
} from "../../hooks/useNodeGroups";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorMessage from "../../components/ui/ErrorMessage";
import { Input, Textarea } from "../../components/ui/Input";

interface GroupFormData {
  name: string;
  description?: string;
  nodesJson?: string;
}

export default function NodeGroupsPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<GroupFormData>({
    name: "",
    description: "",
    nodesJson: "[]",
  });

  const { data, isLoading, isError, refetch } = useNodeGroups({
    page,
    page_size: 100,
    search: search || undefined,
  });
  const createMutation = useCreateNodeGroup();
  const updateMutation = useUpdateNodeGroup();
  const deleteMutation = useDeleteNodeGroup();

  const items = data?.groups || [];

  const openCreate = () => {
    setEditingId(null);
    setFormData({ name: "", description: "", nodesJson: "[]" });
    setShowForm(true);
  };

  const openEdit = (row: any) => {
    setEditingId(row.id);
    setFormData({
      name: row.name,
      description: row.description || "",
      nodesJson: "",
    });
    setShowForm(true);
  };

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: any = {
      name: formData.name,
      description: formData.description || undefined,
    };
    if (!editingId && formData.nodesJson) {
      try {
        const nodes = JSON.parse(formData.nodesJson);
        if (Array.isArray(nodes)) payload.nodes = nodes;
      } catch (_) {}
    }
    if (editingId) {
      await updateMutation.mutateAsync({ id: editingId, data: payload });
    } else {
      await createMutation.mutateAsync(payload);
    }
    setShowForm(false);
    refetch();
  };

  const onDelete = async (id: number) => {
    await deleteMutation.mutateAsync(id);
    refetch();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Global Flows
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage global reusable flows
          </p>
        </div>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          Create Global Flow
        </button>
      </div>

      {/* Search */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-4 sm:space-y-0">
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <Input
            type="text"
            placeholder="Search global flows..."
            variant="shadow-sm"
            showLabel={false}
            className="pl-10"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="text-sm text-gray-500">
          Click a flow to view its nodes
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner />
        </div>
      )}
      {isError && (
        <ErrorMessage
          message="Failed to load global flows"
          onRetry={() => refetch()}
          className="py-12"
        />
      )}

      {!isLoading && !isError && (
        <div className="space-y-4">
          <div className="space-y-4">
            {items.length === 0 ? (
              <div className="text-center py-12">
                <GitBranch className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No global flows found
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {search
                    ? "Try adjusting your search."
                    : "Create a global flow to get started."}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {items.map((row: any) => (
                  <Link
                    to={`/node-groups/${row.id}`}
                    key={row.id}
                    className="block"
                  >
                    <div className="bg-white shadow rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-200">
                      <div className="px-4 py-5 sm:p-6">
                        <div className="flex items-center space-x-3">
                          <ChevronRight className="h-5 w-5 text-gray-400" />
                          <GitBranch className="h-5 w-5 text-blue-500" />
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-medium text-gray-900 truncate">
                              {row.name}
                            </h3>
                            {row.description && (
                              <p className="text-sm text-gray-600 mt-1">
                                {row.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">
                {editingId ? "Edit Global Flow" : "Create Global Flow"}
              </h2>
              <form onSubmit={submitForm} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    value={formData.description || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                {!editingId && (
                  <div>
                    <Textarea
                      label="Inline Nodes (JSON array)"
                      value={formData.nodesJson}
                      onChange={(e) =>
                        setFormData({ ...formData, nodesJson: e.target.value })
                      }
                      variant="rounded-md"
                      className="font-mono"
                      rows={6}
                      placeholder='[{"node_name":"ask_email","messages":{"en":"Please enter your email."}}]'
                    />
                  </div>
                )}
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 bg-gray-100 border rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 text-white rounded"
                  >
                    {editingId ? "Save Changes" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
